import LoadingButton from "@/components/LoadingButton/LoadingButton";
import { Venue, VenueReservation } from "@/graphql/operations";
import useLocalization from "@/hooks/useLocalization";
import useReservation from "@/hooks/useReservation";
import icons from "@/tools/icons";
import { Box, ButtonProps } from "@mui/material";
import { Accessor } from "mt-components";
import { MutableRefObject, useCallback, useRef, useState } from "react";
import { VenueOptionsMenu } from "./VenueOptionsMenu";

type SelectVenueButtonProps = {
  reservationId: VenueReservation["id"];
  venueOptions: Pick<Venue, "id" | "name">[];
  buttonGroupRef?: MutableRefObject<HTMLDivElement | null>;
  disabled?: boolean;
} & Accessor<Venue["id"] | undefined, "selectedVenueId"> &
  ButtonProps;

export function SelectVenueButton({
  buttonGroupRef,
  disabled,
  reservationId,
  venueOptions,
  ...buttonProps
}: SelectVenueButtonProps) {
  const {
    acceptReservation,
    editVenueReservationAnswerMutationResponse: { loading },
  } = useReservation();

  const { t } = useLocalization();

  const loadingButtonContainerRef = useRef<HTMLDivElement | null>(null);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);
  const handleClose = useCallback(() => setAnchorEl(null), []);

  const handleClickButton = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) =>
      setAnchorEl(event.currentTarget),
    []
  );

  const [loadingSelectVenue, setLoadingSelectVenue] = useState(false);

  const handleSelectOption = useCallback(
    async (venueId: Venue["id"]) => {
      handleClose();
      setLoadingSelectVenue(true);
      const venueName = venueOptions.find((v) => v.id === venueId)?.name;
      await acceptReservation(reservationId, venueId, venueName);
      setLoadingSelectVenue(false);
    },
    [handleClose, setLoadingSelectVenue, acceptReservation, reservationId, venueOptions]
  );

  return (
    <>
      <Box ref={loadingButtonContainerRef} sx={{ width: "100%", height: "100%" }}>
        <LoadingButton
          {...buttonProps}
          startIcon={<icons.NoLocation fontSize="large" />}
          endIcon={<icons.ArrowDown />}
          loading={loadingSelectVenue}
          onClick={handleClickButton}
          disabled={disabled || loading}
          aria-label={t("command:selectWhat", {
            interpolation: { what: t("nouns:venue") },
          })}
        >
          <Box sx={{ flex: 1 }}>
            {t("command:selectWhat", {
              interpolation: { what: t("nouns:venue") },
            })}
          </Box>
        </LoadingButton>
      </Box>
      <VenueOptionsMenu
        isMenuOpen={isMenuOpen}
        handleClose={handleClose}
        venueOptions={venueOptions}
        handleSelectOption={handleSelectOption}
        loadingButtonContainerRef={loadingButtonContainerRef}
        buttonGroupRef={buttonGroupRef}
      />
    </>
  );
}
